import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    FormControl,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Theme,
} from '@material-ui/core'
import { FamilyIteration, ProductFamilies } from 'models/projectElementNode'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'

const useStyles = makeStyles((theme: Theme) => ({
    menuItems: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
    },
}))

interface OMVersionDropdownProps {
    options: ProductFamilies[] | FamilyIteration[] | null | undefined
    handleChange: (values: ProductFamilies[] | FamilyIteration) => void
    value: ProductFamilies[] | FamilyIteration | undefined
    multiple?: boolean
    displayEmpty?: boolean
}

const OMVersionDropdown: React.FC<OMVersionDropdownProps> = ({
    options,
    handleChange,
    value,
    multiple = true,
    displayEmpty = true,
}) => {
    const classes = useStyles()

    const [textWidth, setTextWidth] = React.useState(0)

    /**
     * A function to generate a menu item label for a given product family option.
     *
     * @param option - The product family option to generate the label for.
     * @returns A string representing the menu item label.
     */
    const getMenuItemLabel = React.useCallback(
        (option) => {
            const familyNumber = option?.familyId ?? option?.familyNumber
            const iterationName =
                option?.iteration?.name ?? option?.iterationName
            const iterationPart = iterationName ? `- ${iterationName}` : ''

            return `Section ${familyNumber} ${iterationPart}`
        },
        [value]
    )

    const updatedValue = React.useMemo(() => {
        if (!value) return []

        const items = Array.isArray(value)
            ? value.map(getMenuItemLabel)
            : [getMenuItemLabel(value)]

        return items
    }, [value])

    // Scalar for single-select, array for multi-select — avoids MUI treating [] as "uncontrolled"
    const selectValue = React.useMemo(
        () => (multiple ? updatedValue : updatedValue[0] ?? ''),
        [multiple, updatedValue]
    )

    const renderDropdownValue = React.useCallback(
        () => updatedValue.join(', '),
        [updatedValue]
    )

    const optionMap = React.useMemo(() => {
        const map = new Map<string, ProductFamilies | FamilyIteration>()
        options?.forEach((option) => {
            const label = getMenuItemLabel(option)
            map.set(label, option)
        })
        return map
    }, [options])

    const handleSelectionChange = React.useCallback(
        (event) => {
            const currentValue = event.target.value
            const values = Array.isArray(currentValue)
                ? currentValue.map((value) => optionMap.get(value))
                : optionMap.get(currentValue)

            handleChange(values as ProductFamilies[] | FamilyIteration)
        },
        [value, optionMap]
    )

    return (
        <FormControl
            data-testid="omVersionDropdown"
            variant="outlined"
            fullWidth
        >
            <ReactResizeDetector
                handleHeight={false}
                handleWidth={true}
                onResize={(width: number) => setTextWidth(width - 30)}
            />
            <InputLabel id="selectVariant">Select Version</InputLabel>
            <Select
                labelId="selectVariant"
                label="Select Version"
                data-testid="omVersionSelect"
                multiple={multiple}
                displayEmpty={displayEmpty}
                value={selectValue}
                renderValue={renderDropdownValue}
                onChange={handleSelectionChange}
                style={{ width: 'inherit', height: 'auto' }}
                autoFocus={displayEmpty}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    style: {
                        maxHeight: pxToRem(225),
                        width: pxToRem(300),
                    },
                }}
            >
                {!!optionMap && optionMap.size > 0 ? (
                    Array.from(optionMap.entries()).map(([label, option]) => {
                        const menuItemLabel = getMenuItemLabel(option)
                        return (
                            <MenuItem
                                key={label}
                                value={label}
                                className={classes.menuItems}
                            >
                                <TooltipEllipsis
                                    text={menuItemLabel}
                                    width={textWidth}
                                >
                                    {menuItemLabel}
                                </TooltipEllipsis>
                            </MenuItem>
                        )
                    })
                ) : (
                    <MenuItem value="" title="No Options Available" disabled>
                        No Options Available
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    )
}

export default OMVersionDropdown
